import React, { useState } from 'react';
import { toast } from 'sonner';
import { Loader2, MessageSquarePlus, Bug, Lightbulb, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { portalFeedbackSend } from '../services/api';

const CATEGORIES = [
  { value: 'bug', label: 'Hiba', icon: Bug },
  { value: 'idea', label: 'Ötlet', icon: Lightbulb },
  { value: 'other', label: 'Egyéb', icon: MessageCircle },
];

const deviceInfo = () => ({
  ua: navigator.userAgent,
  platform: navigator.platform || '',
  viewport: `${window.innerWidth}x${window.innerHeight}`,
  is_mobile: window.innerWidth < 768,
});

export const FeedbackDialog = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('bug');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (message.trim().length < 5) { toast.error('Írj legalább pár szót.'); return; }
    setBusy(true);
    try {
      await portalFeedbackSend({
        category, message: message.trim(),
        page: window.location.pathname, device: deviceInfo(),
      });
      toast.success('Köszönjük a visszajelzést!');
      setMessage(''); setCategory('bug'); setOpen(false);
    } catch (_) {
      toast.error('Nem sikerült elküldeni. Próbáld újra.');
    } finally { setBusy(false); }
  };

  return (
    <>
      <button
        data-testid="portal-feedback-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 inline-flex items-center gap-2 rounded-full bg-gray-900 text-yellow-400 px-4 py-3 shadow-xl hover:bg-gray-800 transition-colors text-sm font-bold"
      >
        <MessageSquarePlus size={17} />
        <span className="hidden sm:inline">Hibát találtál? Írd meg!</span>
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="portal-feedback-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Visszajelzés küldése</DialogTitle>
            <DialogDescription>
              Írd le röviden, mit tapasztaltál — az eszközöd adatait (mobil/asztali, böngésző) automatikusan csatoljuk.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              {CATEGORIES.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  data-testid={`feedback-category-${value}`}
                  type="button"
                  onClick={() => setCategory(value)}
                  className={`flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-semibold transition-colors ${
                    category === value
                      ? 'border-gray-900 bg-gray-900 text-yellow-400'
                      : 'border-gray-300 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
            <Textarea
              data-testid="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Pl.: Mobilon az edzésterv táblázat kilóg a képernyőről…"
              rows={5}
              maxLength={2000}
            />
          </div>
          <DialogFooter>
            <Button data-testid="feedback-submit" onClick={submit} disabled={busy || message.trim().length < 5}
              className="bg-gray-900 hover:bg-gray-800 text-yellow-400 font-bold w-full sm:w-auto">
              {busy ? <Loader2 className="mr-2 animate-spin" size={15} /> : <MessageSquarePlus className="mr-2" size={15} />}
              Küldés
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
